import { useEffect, useRef, useState } from "react";

export const PULL_THRESHOLD_PX = 72;

function hasScrolledAncestor(target: EventTarget | null, container: HTMLElement): boolean {
  let el = target as HTMLElement | null;
  while (el && el !== container) {
    if (el.scrollTop > 0) return true;
    el = el.parentElement;
  }
  return false;
}
const MAX_PULL_DISTANCE_PX = 108;
const PULL_DAMPING = 0.45;

interface UsePullToRefreshOptions {
  onRefresh: () => Promise<void>;
}

export function usePullToRefresh({ onRefresh }: UsePullToRefreshOptions) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isPulling, setIsPulling] = useState(false);

  const startYRef = useRef(0);
  const isPullingRef = useRef(false);
  const pullDistanceRef = useRef(0);
  const isRefreshingRef = useRef(false);
  const onRefreshRef = useRef(onRefresh);

  useEffect(() => {
    onRefreshRef.current = onRefresh;
  });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // 언마운트 후 async finally 블록의 setState 실행을 방지
    let isMounted = true;

    const resetPull = () => {
      isPullingRef.current = false;
      pullDistanceRef.current = 0;
      if (isMounted) {
        setIsPulling(false);
        setPullDistance(0);
      }
    };

    const onTouchStart = (e: TouchEvent) => {
      // 멀티 터치 시 startYRef 덮어쓰기 방지
      if (e.touches.length !== 1) return;
      if (isRefreshingRef.current || container.scrollTop > 0) return;
      if (hasScrolledAncestor(e.target, container)) return;
      startYRef.current = e.touches[0]!.clientY;
      isPullingRef.current = true;
      setIsPulling(true);
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!isPullingRef.current || isRefreshingRef.current) return;

      // 멀티 터치 전환 시 pull 취소
      if (e.touches.length !== 1) {
        resetPull();
        return;
      }

      if (container.scrollTop > 0) {
        resetPull();
        return;
      }

      const delta = e.touches[0]!.clientY - startYRef.current;
      if (delta <= 0) {
        resetPull();
        return;
      }

      e.preventDefault();
      const clamped = Math.min(MAX_PULL_DISTANCE_PX, delta * PULL_DAMPING);
      pullDistanceRef.current = clamped;
      setPullDistance(clamped);
    };

    const onTouchEnd = async () => {
      if (!isPullingRef.current) return;
      isPullingRef.current = false;
      if (isMounted) setIsPulling(false);

      const distance = pullDistanceRef.current;

      if (distance >= PULL_THRESHOLD_PX) {
        isRefreshingRef.current = true;
        pullDistanceRef.current = PULL_THRESHOLD_PX;
        if (isMounted) {
          setIsRefreshing(true);
          setPullDistance(PULL_THRESHOLD_PX);
        }

        try {
          await onRefreshRef.current();
        } finally {
          isRefreshingRef.current = false;
          pullDistanceRef.current = 0;
          if (isMounted) {
            setIsRefreshing(false);
            setPullDistance(0);
          }
        }
      } else {
        pullDistanceRef.current = 0;
        if (isMounted) setPullDistance(0);
      }
    };

    // OS 인터럽트(전화, 알림 등) 시 새로고침 없이 reset만 수행
    const onTouchCancel = () => {
      resetPull();
    };

    container.addEventListener("touchstart", onTouchStart, { passive: true });
    container.addEventListener("touchmove", onTouchMove, { passive: false });
    container.addEventListener("touchend", onTouchEnd);
    container.addEventListener("touchcancel", onTouchCancel);

    return () => {
      isMounted = false;
      container.removeEventListener("touchstart", onTouchStart);
      container.removeEventListener("touchmove", onTouchMove);
      container.removeEventListener("touchend", onTouchEnd);
      container.removeEventListener("touchcancel", onTouchCancel);
    };
  }, []);

  return { containerRef, pullDistance, isRefreshing, isPulling };
}
